import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-smartphone-line',
    title: 'Design tech que valoriza o produto',
    description: 'Balcões com iluminação LED embutida, expositores com vidro temperado e áreas de unboxing que geram desejo de compra antes do cliente chegar ao caixa.',
  },
  {
    icon: 'ri-lock-line',
    title: 'Segurança integrada ao mobiliário',
    description: 'Expositores com fechadura eletrônica, balcões com compartimentos seguros para estoque e câmeras integradas ao layout. Segurança sem comprometer a experiência.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito da loja',
    description: 'Veja o layout completo da sua loja em 3D antes de comprar qualquer móvel. Posicionamento de expositores, fluxo de clientes e área de unboxing projetados para converter.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipe própria de montagem',
    description: 'Instalação feita pela nossa equipe, no horário que sua loja preferir. Sem terceiros, sem imprevistos. Sua loja pronta para abrir no prazo combinado.',
  },
];

const blocks = [
  {
    title: 'Design Tech para Valorizar o Produto Mais Desejado do Mercado',
    text: [
      'Uma loja de iPhones e smartphones premium não pode ter balcão de madeira velha e expositor de plástico. O produto que você vende custa R$ 5.000, R$ 8.000, R$ 12.000 — e o ambiente precisa comunicar o mesmo nível de sofisticação. Nossos balcões de atendimento têm tampo em vidro temperado de 10mm com iluminação LED embutida que destaca os aparelhos, estrutura em MDF de alta espessura com acabamento em laca brilhante ou fosca, e compartimentos seguros com fechadura para estoque de alto valor.',
      'Os expositores de parede têm prateleiras em vidro temperado com iluminação LED individual para cada aparelho, suportes em acrílico que posicionam os smartphones no ângulo ideal para o cliente interagir, e fechamento com vidro temperado e fechadura eletrônica. O cliente vê, toca, experimenta — e compra.',
    ],
    img: '/media/ia/0349.webp',
    reverse: false,
  },
  {
    title: 'Área de Unboxing que Gera Desejo e Fideliza o Cliente',
    text: [
      'O unboxing é um momento de emoção — e pode ser um poderoso gerador de conteúdo para redes sociais. Nossa área de unboxing tem mesa com tampo em mármore ou granito que serve de fundo para fotos e vídeos, iluminação LED com temperatura de cor neutra (4000K) que garante qualidade de imagem, e painel de fundo com o logo da loja que aparece em todas as fotos postadas pelo cliente.',
      'Também projetamos a área de assistência técnica com bancadas de trabalho com iluminação técnica, organizadores para ferramentas e peças, e divisórias que separam a área de atendimento da área de reparo. Tudo com o mesmo padrão visual da loja — porque a experiência do cliente não termina na compra.',
    ],
    img: '/media/ia/0350.webp',
    reverse: true,
  },
  {
    title: 'Lojas de Celular que Fecham Mais Vendas com o Ambiente Certo',
    text: [
      'Já atendemos lojas de celular em Caruaru e região que viram o ticket médio aumentar depois de reformar o ambiente. Quando o cliente entra em uma loja que parece Apple Store — com expositores iluminados, balcão de vidro e área de unboxing — ele inconscientemente associa o ambiente à qualidade do produto e fica mais disposto a pagar o preço cheio.',
      'Nosso projeto 3D gratuito mostra exatamente como vai ficar sua loja antes de você investir um real. Posicionamento de expositores, fluxo de clientes, área de espera e balcão de atendimento — tudo projetado para maximizar a conversão. E com nossa equipe própria de montagem, sua loja fica pronta no prazo combinado, sem surpresas.',
    ],
    img: '/media/ia/0351.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0352.webp',
    alt: 'Balcão de atendimento para loja de celular',
  },
  {
    src: '/media/ia/0353.webp',
    alt: 'Expositor de parede iluminado',
  },
  {
    src: '/media/ia/0354.webp',
    alt: 'Área de unboxing premium',
  },
  {
    src: '/media/ia/0355.webp',
    alt: 'Bancada de assistência técnica',
  },
];

export default function TecnologiaPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Lojas de Celular e Tecnologia | Caruaru PE',
    description: 'Balcões iluminados, expositores com vidro e área de unboxing para lojas de celular e tecnologia. Projeto 3D gratuito. Caruaru e Agreste PE.',
    keywords: 'mobiliário loja celular Recife, expositor iPhone Petrolina, balcão loja tecnologia Garanhuns, móveis loja smartphone Arcoverde, expositor vidro celular Sertão PE, balcão atendimento loja Pernambuco, mobiliário loja eletrônicos Agreste, mobiliário tecnologia Nordeste, balcão iluminado celular Recife, expositor smartphone Petrolina, mobiliário loja iPhone Garanhuns, mobiliário tecnologia Caruaru, balcão atendimento Bezerros',
    path: '/tecnologia',
  });

  useSchemaLD({
    id: 'schema-tecnologia',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Lojas de Celular e Tecnologia | Caruaru PE',
      description: 'Balcões iluminados, expositores com vidro e área de unboxing para lojas de celular e tecnologia. Projeto 3D gratuito.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/tecnologia`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Lojas de Tecnologia',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0356.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Tecnologia · Lojas de Celular · iPhones
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Design tech para valorizar o produto mais desejado do mercado.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Balcões com iluminação LED, expositores com vidro temperado e área de unboxing que gera desejo de compra. Porque o ambiente da sua loja precisa ser tão premium quanto o produto que você vende.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Lojas%20de%20Tecnologia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que lojas de tecnologia escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Experiência Premium</span> do Expositor ao Unboxing
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Lojas%20de%20Tecnologia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">Veja sua loja em 3D antes de investir um real.</h2>
          <p className="text-gray-400 text-base mb-10">Nossa equipe visita sua loja, mede o espaço e entrega o projeto 3D em até 48h. Você aprova cada detalhe — expositores, balcão e área de unboxing — antes de fabricarmos.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Lojas%20de%20Tecnologia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Mobiliário para <span className="text-[#E8610A]">Lojas de Tecnologia</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Lojas%20de%20Tecnologia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Lojas%20de%20Tecnologia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
